import React, { useState } from 'react';
import { Form, Input, Radio, Select, Checkbox, Card, Alert } from 'antd';
import {
  CarOutlined,
  BankOutlined,
  FileDoneOutlined,
  PhoneOutlined,
  UserOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { ALL_SRI_LANKA_DISTRICTS } from '../../../../Utils/sriLankaGeoData';

const { Option } = Select;
const { TextArea } = Input;

const Section10CertificateDelivery = ({ form }) => {
  const [deliveryMethod, setDeliveryMethod] = useState(form.getFieldValue('deliveryMethod') || 'courier');

  const handleMethodChange = (e) => {
    setDeliveryMethod(e.target.value);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <span className="section-badge">SECTION 10</span>
        <h3>Certificate Delivery Information</h3>
        <p className="section-description">
          Please specify how you wish to receive your official graduation booklet and physical certificate.
        </p>
      </div>

      <div className="form-grid">
        <Form.Item
          label="Preferred Delivery Method"
          name="deliveryMethod"
          rules={[{ required: true, message: 'Please select a delivery method' }]}
          initialValue="courier"
        >
          <Radio.Group
            onChange={handleMethodChange}
            buttonStyle="solid"
            size="large"
            className="delivery-method-radio-group"
          >
            <Radio.Button value="courier" className="delivery-method-radio-btn">
              <CarOutlined className="method-icon" />
              <strong>Courier Delivery</strong>
              <div className="method-desc">Delivered to your doorstep</div>
            </Radio.Button>

            <Radio.Button value="collect_from_institute" className="delivery-method-radio-btn">
              <BankOutlined className="method-icon" />
              <strong>Collect from Institute</strong>
              <div className="method-desc">Collect at IBAS Colombo Center</div>
            </Radio.Button>

            <Radio.Button value="digital_only" className="delivery-method-radio-btn">
              <FileDoneOutlined className="method-icon" />
              <strong>Digital Certificate</strong>
              <div className="method-desc">E-Certificate with QR verification</div>
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {deliveryMethod === 'collect_from_institute' && (
          <Alert
            type="info"
            showIcon
            message="Institute Collection Notice"
            description="You will be notified via SMS and WhatsApp once your certificate has been printed and is ready for collection at the IBAS Main Academic Center."
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        {deliveryMethod === 'digital_only' && (
          <Alert
            type="info"
            showIcon
            message="Digital Certificate Selected"
            description="Your authenticated digital certificate with verifiable QR code will be generated directly in your student portal under the Certificates tab."
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        {deliveryMethod === 'courier' && (
          <Card className="courier-address-card" bordered={false}>
            <h4 style={{ margin: '0 0 16px', color: '#1f2937' }}>Doorstep Courier Delivery Destination</h4>

            <Form.Item
              label="Certificate Delivery Street Address"
              name="deliveryAddress"
              rules={[{ required: deliveryMethod === 'courier', message: 'Please enter the courier delivery address' }]}
            >
              <TextArea rows={2} placeholder="House / Flat / Office Number, Street Name, Landmark" />
            </Form.Item>

            <div className="grid-two-cols">
              <Form.Item
                label="Nearest City / Town"
                name="nearestCity"
                rules={[{ required: deliveryMethod === 'courier', message: 'Please enter the nearest city' }]}
              >
                <Input prefix={<EnvironmentOutlined />} placeholder="e.g. Maharagama, Wattala, Gampaha" size="large" />
              </Form.Item>

              <Form.Item
                label="District"
                name="deliveryDistrict"
                rules={[{ required: deliveryMethod === 'courier', message: 'Please select delivery district' }]}
              >
                <Select size="large" placeholder="Select Delivery District" showSearch>
                  {ALL_SRI_LANKA_DISTRICTS.map((d) => (
                    <Option key={d} value={d}>
                      {d}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item label="Postal Code (Recommended)" name="deliveryPostalCode">
              <Input placeholder="e.g. 10280" size="large" />
            </Form.Item>
          </Card>
        )}

        {/* Recipient Details */}
        <div className="grid-two-cols">
          <Form.Item
            label="Recipient Full Name"
            name="recipientName"
            rules={[{ required: true, message: 'Please enter recipient name' }]}
            extra="Person authorized to receive the physical package"
          >
            <Input prefix={<UserOutlined />} placeholder="e.g. Kasun Chamara Perera" size="large" />
          </Form.Item>

          <Form.Item
            label="Delivery Contact Number"
            name="deliveryContact"
            rules={[
              { required: true, message: 'Please enter delivery contact number' },
              { pattern: /^[0-9+-\s()]{9,15}$/, message: 'Please enter a valid phone number' }
            ]}
            extra="Courier rider will call this number prior to arrival"
          >
            <Input prefix={<PhoneOutlined />} placeholder="e.g. 0771234567" size="large" />
          </Form.Item>
        </div>

        <div className="grid-two-cols">
          <Form.Item label="Recipient WhatsApp Number (Optional)" name="deliveryWhatsapp">
            <Input placeholder="e.g. 0771234567" size="large" />
          </Form.Item>

          <Form.Item label="Recipient Email Address (Optional)" name="deliveryEmail">
            <Input type="email" placeholder="e.g. student@gmail.com" size="large" />
          </Form.Item>
        </div>

        <Form.Item label="Special Delivery Instructions (Optional)" name="specialInstructions">
          <TextArea rows={2} placeholder="e.g. Call before delivery, deliver after 2:00 PM, or leave with security" />
        </Form.Item>

        <Form.Item
          name="deliveryDetailsConfirmed"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error('Please confirm your delivery details')),
            },
          ]}
          style={{ marginBottom: 0 }}
        >
          <Checkbox>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              I confirm that the above delivery details are accurate and complete.
            </span>
          </Checkbox>
        </Form.Item>
      </div>
    </div>
  );
};

export default Section10CertificateDelivery;
